"use client";

import {createButton} from "@gluestack-ui/button";
import {PrimitiveIcon} from "@gluestack-ui/icon";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {withStates} from "@gluestack-ui/nativewind-utils/withStates";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {withStyleContextAndStates} from "@gluestack-ui/nativewind-utils/withStyleContextAndStates";
import {cssInterop} from "nativewind";
import type {Ref} from "react";
import React, {useMemo} from "react";
import type {PressableProps} from "react-native";
import {ActivityIndicator, Platform, Pressable, Text, View} from "react-native";

import {useColors} from "../../../hooks/useColors";

import {buttonGroupStyle, buttonIconStyle, buttonStyle, buttonTextStyle} from "./styles";

const SCOPE = "BUTTON";
const ButtonWrapper = React.forwardRef<React.ElementRef<typeof Pressable>, PressableProps>(
    ({...props}, ref) => {
        return <Pressable {...props} ref={ref} />;
    }
);

ButtonWrapper.displayName = "ButtonWrapper";

const Root =
    Platform.OS === "web"
        ? withStyleContext(ButtonWrapper, SCOPE)
        : withStyleContextAndStates(ButtonWrapper, SCOPE);

const UIButton = createButton({
    Root,
    Text,
    Group: View,
    Spinner: ActivityIndicator,
    Icon: withStates(PrimitiveIcon)
});

cssInterop(Root, {className: "style"});
cssInterop(UIButton.Text, {className: "style"});
cssInterop(UIButton.Group, {className: "style"});
cssInterop(UIButton.Spinner, {
    className: {target: "style", nativeStyleToProp: {color: true}}
});

cssInterop(PrimitiveIcon, {
    className: {
        target: "style",
        nativeStyleToProp: {
            height: true,
            width: true,
            // @ts-ignore
            fill: true,
            color: "classNameColor",
            stroke: true
        }
    }
});

type BaseButtonProps = Omit<React.ComponentPropsWithoutRef<typeof UIButton>, "context"> &
    VariantProps<typeof buttonStyle> & {className?: string};
export type IButtonProps<T = never> = IsNever<T> extends true
    ? BaseButtonProps
    : React.ComponentProps<"button"> & BaseButtonProps;

const Button = React.forwardRef<React.ElementRef<typeof UIButton>, IButtonProps>(
    (
        {
            className,
            size = "md",
            variant = "solid",
            action = "primary",
            // todo
            // I prefer to remove this prop moving forward
            no110MinWidth = false,
            ...props
        },
        ref
    ) => {
        return (
            <UIButton
                ref={ref}
                {...props}
                className={buttonStyle({size, action, variant, no110MinWidth, class: className})}
                context={{size, action, variant}}
            />
        );
    }
) as {
    <T extends "web">(
        props: IButtonProps<T> & {ref?: Ref<React.ElementRef<typeof UIButton>>}
    ): React.ReactElement<any>;
    displayName: string;
};

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
    VariantProps<typeof buttonTextStyle> & {className?: string};

const ButtonText = React.forwardRef<React.ElementRef<typeof UIButton.Text>, IButtonTextProps>(
    ({className, size, action, ...props}, ref) => {
        const {
            size: parentSize,
            action: parentAction,
            variant: parentVariant
        } = useStyleContext(SCOPE);

        return (
            <UIButton.Text
                ref={ref}
                {...props}
                className={buttonTextStyle({
                    parentVariants: {
                        size: parentSize,
                        action: parentAction,
                        variant: parentVariant
                    },
                    size,
                    action,
                    class: className
                })}
            />
        );
    }
);

const ButtonSpinner = React.forwardRef<React.ElementRef<typeof UIButton.Spinner>, IButtonTextProps>(
    ({className, size, action, ...props}, ref) => {
        const colors = useColors();
        const {variant: parentVariant, action: parentAction} = useStyleContext(SCOPE);

        const color = useMemo(
            () =>
                ((
                    {
                        solid: "white",
                        outline: colors["--color-blueGray-600"],
                        icon: colors["--color-blueGray-600"],
                        ghost:
                            parentAction === "primary"
                                ? colors["--color-blue-600"]
                                : colors["--color-error-600"],
                        link: colors["--color-blue-600"]
                    } satisfies {
                        [key in Required<IButtonTextProps>["variant"]]: string;
                    }
                )[parentVariant as Required<IButtonTextProps>["variant"]]),
            [colors, parentVariant, parentAction]
        );

        return (
            // TODO: should double check why this throws mismatch type recently
            <UIButton.Spinner ref={ref} {...props} className={className} color={color} />
        );
    }
);

type IButtonIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
    VariantProps<typeof buttonIconStyle> & {
        className?: string | undefined;
        as: React.ElementType;
    };

const ButtonIcon = React.forwardRef<React.ComponentRef<typeof UIButton.Icon>, IButtonIcon>(
    ({className, size, as: Component, ...props}, ref) => {
        const {
            size: parentSize,
            action: parentAction,
            variant: parentVariant
        } = useStyleContext(SCOPE);
        if (typeof size === "number") {
            return (
                <Component
                    ref={ref}
                    {...props}
                    className={buttonIconStyle({
                        action: parentAction,
                        variant: parentVariant,
                        class: className
                    })}
                    size={size}
                />
            );
        }
        if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
            return (
                <Component
                    ref={ref}
                    {...props}
                    className={buttonIconStyle({
                        action: parentAction,
                        variant: parentVariant,
                        class: className
                    })}
                />
            );
        }
        return (
            <Component
                ref={ref}
                className={buttonIconStyle({
                    parentVariants: {
                        size: parentSize,
                        action: parentAction,
                        variant: parentVariant
                    },
                    size,
                    class: className
                })}
            />
        );
    }
);

type IButtonGroupProps = React.ComponentPropsWithoutRef<typeof UIButton.Group> &
    VariantProps<typeof buttonGroupStyle>;

const ButtonGroup = React.forwardRef<React.ElementRef<typeof UIButton.Group>, IButtonGroupProps>(
    ({className, space = "md", isAttached = false, ...props}, ref) => {
        return (
            <UIButton.Group
                className={buttonGroupStyle({class: className, space, isAttached})}
                {...props}
                ref={ref}
            />
        );
    }
);

Button.displayName = "Button";
ButtonText.displayName = "ButtonText";
ButtonSpinner.displayName = "ButtonSpinner";
ButtonIcon.displayName = "ButtonIcon";
ButtonGroup.displayName = "ButtonGroup";

export {Button, ButtonText, ButtonSpinner, ButtonIcon, ButtonGroup};
